'use client';

import {Dialog} from '@headlessui/react';
import {noop, omitEventDefaultBehavior, withEventBehaviourOmitted} from '@rocket-ui/tools';
import React, {ComponentType, ReactNode, SVGProps} from 'react';
import {NewButton} from '../Button';

type Props = {
	isOpen: boolean;
	title?: ReactNode;
	description: ReactNode;
	confirmText: ReactNode;
	options?: Record<string, ReactNode>;
	onConfirm: (value: string | void) => void;
	cancelText?: ReactNode;
	onCancel?: VoidFunction;
	size?: 'sm' | 'md' | 'lg';
	illustration?: ComponentType<SVGProps<SVGSVGElement>>;
};

export const ConfirmationModal = ({
	isOpen,
	title,
	description,
	confirmText,
	options = {},
	onConfirm,
	cancelText,
	onCancel = noop,
	size = 'md',
	illustration: Illustration,
}: Props): React.ReactElement => (
	<Dialog open={isOpen} onClose={onCancel} className="relative z-50">
		<div className="fixed inset-0 bg-black/30" aria-hidden="true" />

		<div className="fixed inset-0 flex items-center justify-center p-4">
			<Dialog.Panel className="flex flex-col w-full max-w-sm rounded-md bg-white p-8" data-testid={'modal'}>
				{Illustration ? <Illustration className="mx-auto mb-4 h-[88px] w-[72px]" aria-hidden /> : null}

				<Dialog.Title className={size === 'sm' ? 'text-sm' : 'text-2xl font-bold mb-6'}>{title}</Dialog.Title>

				<div className={'pb-6'}>{description}</div>

				<div className={'flex w-full gap-4'}>
					{Object.entries(options)?.map(([key, value]) => (
						<div key={key} className={'flex-auto'}>
							<NewButton type={'button'} onClick={() => onConfirm(key)} className={'flex-1'}>
								{value}
							</NewButton>
						</div>
					))}
					{confirmText && (
						<NewButton
							type={'button'}
							variant={'tonal'}
							className={'flex-auto'}
							onClick={(e) => {
								omitEventDefaultBehavior(e);
								onConfirm();
							}}
							data-testid={'confirm-button'}
						>
							{confirmText}
						</NewButton>
					)}
					{cancelText && (
						<NewButton
							type={'button'}
							className={'flex-1'}
							onClick={withEventBehaviourOmitted(onCancel)}
							data-testid={'cancel-button'}
						>
							{cancelText}
						</NewButton>
					)}
				</div>
			</Dialog.Panel>
		</div>
	</Dialog>
);
